import { addDays, isSameDay, isToday, isWeekend } from "date-fns";

/** Return the next weekday after a given date.
 * @param date The date of reference.
 * @return The next weekday after date.
 */
export const nextWeekday = (date: Date) => {
  switch (date.getDay()) {
    default:
      // Sunday, Mon-Thu, return next day
      return addDays(date, 1);
    case 5: // Friday, return next Monday
      return addDays(date, 3);
    case 6: // Saturday, return next Monday
      return addDays(date, 2);
  }
};

/**
 * Get the next work day after the given date by excluding weekends and given holidays.
 * @param date The date of reference
 * @param excludedDays The dates that should be excluded along with the weekends.
 * @returns The next working day after the date.
 */
export const nextWorkDay = (date: Date, excludedDays: Date[]) => {
  let workdayFound = false;
  let possibleNextWorkDay = date;

  do {
    possibleNextWorkDay = nextWeekday(possibleNextWorkDay);
    if (!excludedDays.some((day) => isSameDay(possibleNextWorkDay, day))) {
      workdayFound = true;
    }
  } while (!workdayFound);
  return possibleNextWorkDay;
};

/** Return the minimum selectable receipt date.
 * The currentLocal is used to determine the current date.
 * @param referenceDate The date to be worked with instead of the current date. If not given, the current date will be used.
 * @return If the current time is before 12:00 PM Icelandic is a weekday return the current day, otherwise return the next weekday.
 */
export const getMinReceiptDate = (referenceDate?: Date | null) => {
  const currentLocalDate = new Date();
  const date = referenceDate || currentLocalDate;
  // Icelandic time is in UTC+0, so it's safe to just use UTC hours.
  return currentLocalDate.getUTCHours() < 12 && !isWeekend(date)
    ? date
    : nextWeekday(date);
};

/** Return the minimum selectable delivery date.
 * The currentLocal is used to determine the current date.
 * @param referenceDate The date to be worked with instead of the current date. If not given, the current date will be used.
 * @return If the reference date is today and current time is after 4:00 PM Icelandic return the next second weekday,
 *  otherwise return the next weekday.
 */
export const getMinDeliveryDate = (referenceDate?: Date | null) => {
  const currentLocalDate = new Date();
  const date = referenceDate || currentLocalDate;
  // Icelandic time is in UTC+0, so it's safe to just use UTC hours.
  return isToday(date) && currentLocalDate.getUTCHours() >= 16
    ? nextWeekday(nextWeekday(date))
    : nextWeekday(date);
};

/** Convert the given date to UTC, without taking the timezone into consideration.
 * Ex. LocalDate is Nov 16 2022 00:00:00 GMT+0200 => The return will be Nov 16 2022 00:00:00 GMT
 * @param localDate The date to copy into UTC.
 * */
export const dateToUTC_WithoutTimezoneChange = (localDate: Date) =>
  new Date(
    Date.UTC(localDate.getFullYear(), localDate.getMonth(), localDate.getDate())
  );

/** Merges a delivery date and a delivery time into a single Date object in UTC
 * Ex. deliveryDate is Nov 16 2022 00:00:00 GMT+0200 and deliveryTime is Nov 10 2022 13:00:00 GMT+0200=> The return will be Nov 16 2022 13:00:00 GMT+0000.
 * @param deliveryDate The date part of the deliveryDate is taken from this date param, with the time component typically set to 00:00:00.
 * @param deliveryTime The time part of the deliveryTime is taken from this date param, which may include a date but only the time component is used.
 * @returns A Date object representing the merged date and time in UTC.
 */
export const mergeDateAndTimeToUTC_WithoutTimezoneChange = (
  deliveryDate: Date,
  deliveryTime: Date
): Date => {
  return new Date(
    Date.UTC(
      deliveryDate.getFullYear(),
      deliveryDate.getMonth(),
      deliveryDate.getDate(),
      deliveryTime.getHours(),
      deliveryTime.getMinutes(),
      deliveryTime.getSeconds(),
      deliveryTime.getMilliseconds()
    )
  );
};
